import { FC, FormEvent, ReactNode, useState } from 'react'
import { History, Restore, SaveAlt, Visibility, VisibilityOff } from '@mui/icons-material'
import { Alert, Box, Button, IconButton, Paper, Tooltip, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

interface DraftProps {
  hasDraft: boolean
  draftSavedAt?: number
  onRestoreDraft: () => void
  onDiscardDraft: () => void
  onSaveDraft: () => void
}

interface MobileConfiguratorLayoutProps {
  title: string
  isEdit: boolean
  onSubmit: (e: FormEvent) => void
  onCancel: () => void
  onReset?: () => void
  isDirty?: boolean
  children: ReactNode
  previewComponent?: ReactNode
  errors?: string[]
  draftProps?: DraftProps
}

const formatDraftTime = (savedAt: number): string => {
  const date = new Date(savedAt)

  return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export const MobileConfiguratorLayout: FC<MobileConfiguratorLayoutProps> = ({
  title,
  isEdit,
  onSubmit,
  onCancel,
  onReset,
  isDirty = false,
  children,
  previewComponent,
  errors = [],
  draftProps,
}) => {
  const [previewVisible, setPreviewVisible] = useState(true)

  return (
    <PageContentLayout
      sx={{ display: 'flex', flexDirection: 'row', height: '100vh', p: 0, overflow: 'hidden' }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
          }}
        >
          <PageTitle
            end={false}
            start={
              <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                {title}
              </Typography>
            }
          />

          {draftProps?.hasDraft && (
            <Alert
              severity='info'
              icon={<History />}
              sx={{ mb: 2 }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size='small' color='inherit' onClick={draftProps.onDiscardDraft}>
                    Delete
                  </Button>
                </Box>
              }
            >
              Draft
              {draftProps.draftSavedAt ? ` ${formatDraftTime(draftProps.draftSavedAt)}` : ''}
            </Alert>
          )}

          {errors.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light' }}>
              {errors.map((error, index) => (
                <Typography key={index} color='error.contrastText'>
                  {error}
                </Typography>
              ))}
            </Paper>
          )}

          <form onSubmit={onSubmit}>{children}</form>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 2,
            px: 3,
            display: 'flex',
            gap: 2,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            alignItems: 'center',
          }}
        >
          <Button
            type='submit'
            variant='contained'
            color='primary'
            onClick={onSubmit}
            disabled={!isDirty}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
          {onReset && (
            <Button
              variant='outlined'
              color='warning'
              onClick={onReset}
              disabled={!isDirty}
            >
              Reset
            </Button>
          )}
          <Button variant='outlined' onClick={onCancel}>
            Cancel
          </Button>
          {draftProps && (
            <Tooltip title='Save current changes as a local draft'>
              <Button
                variant='outlined'
                color='secondary'
                startIcon={<SaveAlt />}
                onClick={draftProps.onSaveDraft}
                disabled={!isDirty}
              >
                Save as Draft
              </Button>
            </Tooltip>
          )}
          {previewComponent && (
            <Tooltip title={previewVisible ? 'Hide Preview' : 'Show Preview'}>
              <IconButton onClick={() => setPreviewVisible(!previewVisible)} color='primary'>
                {previewVisible ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {previewVisible && previewComponent && (
        <Box
          sx={{
            width: 400,
            flexShrink: 0,
            overflow: 'auto',
            p: 2,
            borderLeft: '1px solid',
            borderColor: 'divider',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {previewComponent}
        </Box>
      )}
    </PageContentLayout>
  )
}
